"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";

export const Dropdown = ({
  trigger,
  children,
  align = "right",
  onOpenChange,
  minWidth = "140px",
  maxHeight = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onOpenChange?.(newState);
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  const alignStyles = {
    left: { left: 0 },
    right: { right: 0 },
    center: { left: "50%", transform: "translateX(-50%)" },
  };

  const dropdownMenuStyles = {
    position: "absolute",
    top: "100%",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    zIndex: 50,
    minWidth: minWidth,
    marginTop: "8px",
    overflow: "hidden",
    ...alignStyles[align],
  };

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      ref={dropdownRef}
    >
      <div onClick={toggleDropdown}>{trigger}</div>

      {isOpen && (
        <div style={dropdownMenuStyles}>
          <div
            style={{
              padding: "4px 0",
              maxHeight: maxHeight || "none",
              overflowY: maxHeight ? "auto" : "visible",
            }}
          >
            {React.Children.map(children, (child) => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  onClick: (...args) => {
                    child.props.onClick?.(...args);
                    closeDropdown();
                  },
                });
              }
              return child;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const DropdownItem = ({
  children,
  onClick,
  icon: Icon,
  variant = "default",
  style = {},
  ...props
}) => {
  const variantStyles = {
    default: {
      color: "#374151",
      hover: "#f3f4f6",
      active: "#f3f4f6",
    },
    destructive: {
      color: "#EF4444",
      hover: "#FEE2E2",
      active: "#FEE2E2",
    },
    selected: {
      color: "#F97316",
      hover: "#FFF7ED",
      active: "#FFF7ED",
      fontWeight: 500,
    },
  };

  const itemStyles = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    padding: "10px 14px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "14px",
    textAlign: "left",
    transition: "all 150ms ease",
    color: variantStyles[variant].color,
    textDecoration: "none",
    fontWeight: variantStyles[variant].fontWeight || 400,
    ...style,
  };

  const handleMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = variantStyles[variant].hover;
    e.currentTarget.style.color =
      variant === "selected" ? "#F97316" : "#374151";
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = "transparent";
    e.currentTarget.style.color = variantStyles[variant].color;
  };

  const handleMouseDown = (e) => {
    e.currentTarget.style.backgroundColor = variantStyles[variant].active;
  };

  const handleMouseUp = (e) => {
    e.currentTarget.style.backgroundColor = variantStyles[variant].hover;
  };

  return (
    <button
      style={itemStyles}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...props}
    >
      {Icon && <Icon size={16} style={{ flexShrink: 0 }} />}
      <span style={{ flex: 1, whiteSpace: "nowrap" }}>{children}</span>
    </button>
  );
};
